import { useForm } from '@inertiajs/react'

import { TextInput } from '@atoms'
import { Accordion } from '@components/atoms/Accordion'
import Alert from '@components/atoms/Alert'
import Badge from '@components/atoms/Badge'
import Link from '@components/atoms/Link'
import { ConfigurationsLayout } from '@components/configurations/ConfigurationsLayout'
import { EmailTemplateForm, type EmailTemplate } from '@components/configurations/EmailTemplateForm'
import { SettingsAccordionItem } from '@components/configurations/SettingsAccordionItem'
import { SettingsCard } from '@components/configurations/SettingsCard'
import { SettingsForm, textField } from '@components/configurations/SettingsForm'
import { SettingsSwitches, type SettingSwitch } from '@components/configurations/SettingsSwitches'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('configurations.mailers')

const ACTION = '/app/settings/mailers'

type SenderDomain = { name: string; verified: boolean; exists: boolean; href: string }

export type MailersShowProps = {
  values: Record<string, string>
  switches: SettingSwitch[]
  senderDomain: SenderDomain
}

type Template = EmailTemplate & { switchKey?: string }

const NEW_SUPPORTER_VARIABLES = {
  first_name: '[%first_name%]',
  last_name: '[%last_name%]',
  group_name: '[%supporter_group.name%]',
}

const TEMPLATES: Template[] = [
  {
    id: 'email_blast_approval',
    keys: ['EMAIL_BLAST_APPROVAL_SUBJECT', 'EMAIL_BLAST_APPROVAL_CONTENT'],
    richTextKey: 'EMAIL_BLAST_APPROVAL_CONTENT',
    variables: { blast_subject: '[%blast.subject%]', blast_url: '[%blast.url%]' },
  },
  {
    id: 'sms_blast_approval',
    keys: ['SMS_BLAST_APPROVAL_SUBJECT', 'SMS_BLAST_APPROVAL_CONTENT'],
    richTextKey: 'SMS_BLAST_APPROVAL_CONTENT',
    variables: { blast_body: '[%blast.body%]', blast_url: '[%blast.url%]' },
  },
  {
    id: 'member_removal',
    keys: ['MEMBER_REMOVAL_SUBJECT', 'MEMBER_REMOVAL_CONTENT'],
    richTextKey: 'MEMBER_REMOVAL_CONTENT',
    variables: {
      group_name: '[%supporter_group.name%]',
      supporter_name: '[%supporter.name%]',
      requester_name: '[%requester.name_or_email%]',
    },
  },
  { id: 'footer', keys: ['EMAIL_FOOTER_HTML'], richTextKey: 'EMAIL_FOOTER_HTML', variables: {} },
  {
    id: 'new_supporter_autoresponse',
    switchKey: 'SEND_NEW_SUPPORTER_AUTORESPONSE',
    keys: [
      'NEW_SUPPORTER_AUTORESPONSE_SENDER_NAME',
      'NEW_SUPPORTER_AUTORESPONSE_SENDER_EMAIL',
      'NEW_SUPPORTER_AUTORESPONSE_SUBJECT',
      'NEW_SUPPORTER_AUTORESPONSE_CONTENT',
    ],
    richTextKey: 'NEW_SUPPORTER_AUTORESPONSE_CONTENT',
    variables: NEW_SUPPORTER_VARIABLES,
  },
  {
    id: 'new_supporter_notification',
    switchKey: 'SEND_NEW_SUPPORTER_NOTIFICATION',
    keys: ['NEW_SUPPORTER_NOTIFICATION_SUBJECT', 'NEW_SUPPORTER_NOTIFICATION_CONTENT'],
    richTextKey: 'NEW_SUPPORTER_NOTIFICATION_CONTENT',
    variables: NEW_SUPPORTER_VARIABLES,
  },
]

function SenderForm({ values, senderDomain }: Pick<MailersShowProps, 'values' | 'senderDomain'>) {
  const form = useForm({ MAILER_SENDER: values.MAILER_SENDER })
  const { name, verified, exists, href } = senderDomain

  return (
    <SettingsForm form={form} action={ACTION}>
      <TextInput
        type="email"
        label={t('sender.email')}
        description={
          verified && values.MAILER_SENDER && !form.isDirty ? t('sender.verified', { domain: name }) : undefined
        }
        {...textField(form, 'MAILER_SENDER')}
      />
      {!verified && (
        <Alert type="info" size="sm">
          <p className="tw:m-0">{t('sender.unverified', { domain: name })}</p>
          <p className="tw:m-0">
            <Link href={href} rank="link" size="sm" className="tw:p-0">
              {t(exists ? 'sender.verify_link' : 'sender.add_link', { domain: name })}
            </Link>
          </p>
        </Alert>
      )}
    </SettingsForm>
  )
}

export default function MailersShow({ values, switches, senderDomain }: MailersShowProps) {
  const switchesFor = (keys: string[]) => switches.filter(({ key }) => keys.includes(key))
  const isOn = (key: string) => switches.some((setting) => setting.key === key && setting.checked)
  const templates = TEMPLATES.filter(({ keys }) => keys.every((key) => key in values))

  return (
    <ConfigurationsLayout>
      <SettingsCard heading={t('sender.heading')}>
        <SenderForm values={values} senderDomain={senderDomain} />
      </SettingsCard>
      <SettingsCard heading={t('inbox.heading')}>
        <SettingsSwitches action={ACTION} switches={switchesFor(['ENABLE_EMAIL_INBOX_FEATURE'])} t={t} />
      </SettingsCard>
      <SettingsCard heading={t('templates.heading')}>
        <Accordion type="multiple" className="tw:px-5">
          {templates.map((template) => (
            <SettingsAccordionItem
              key={template.id}
              value={template.id}
              trigger={
                <span className="tw:flex tw:items-center tw:gap-2">
                  {t(`templates.${template.id}`)}
                  {template.switchKey && (
                    <Badge color={isOn(template.switchKey) ? 'success' : 'muted'}>
                      {t(isOn(template.switchKey) ? 'templates.on' : 'templates.off')}
                    </Badge>
                  )}
                </span>
              }
            >
              {template.switchKey && (
                <div className="tw:border-b tw:border-gray-cool-100">
                  <SettingsSwitches action={ACTION} switches={switchesFor([template.switchKey])} t={t} />
                </div>
              )}
              <EmailTemplateForm action={ACTION} values={values} template={template} t={t} />
            </SettingsAccordionItem>
          ))}
        </Accordion>
      </SettingsCard>
    </ConfigurationsLayout>
  )
}
